import type { GpsSample } from '../xrk/types'
import type { LapTrace } from './laps'

const G = 9.80665
/** Readings beyond this are GPS noise or a crash, not riding. */
export const ACCEL_CAP_G = 2
/** Within this of zero the bike is coasting: shown in the neutral colour. */
export const ACCEL_DEAD_BAND_G = 0.05

/**
 * Longitudinal acceleration in g at each GPS sample, from the change of GPS speed over time,
 * smoothed over about half a second to take the edge off the receiver's noise.
 */
export function gpsAccel(gps: GpsSample[]): Float64Array {
  const n = gps.length
  const raw = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const a = gps[Math.max(0, i - 1)]
    const b = gps[Math.min(n - 1, i + 1)]
    const dt = b.time - a.time
    raw[i] = dt > 0 ? (b.speed - a.speed) / dt / G : 0
  }
  const out = new Float64Array(n)
  const half = 2
  for (let i = 0; i < n; i++) {
    let sum = 0
    let count = 0
    for (let j = Math.max(0, i - half); j <= Math.min(n - 1, i + half); j++) {
      sum += raw[j]
      count++
    }
    out[i] = sum / count
  }
  return out
}

/** The colour scale's ends: the strongest acceleration and braking among the laps, capped. */
export interface AccelScale {
  maxAccel: number
  maxBrake: number
}

export function accelScale(accels: Float64Array[]): AccelScale {
  let maxAccel = 0
  let maxBrake = 0
  for (const a of accels)
    for (let i = 0; i < a.length; i++) {
      if (a[i] > maxAccel) maxAccel = a[i]
      if (-a[i] > maxBrake) maxBrake = -a[i]
    }
  return { maxAccel: Math.min(ACCEL_CAP_G, maxAccel), maxBrake: Math.min(ACCEL_CAP_G, maxBrake) }
}

export const ACCEL_NEUTRAL = '#8b93a7'
const ACCEL_GREEN = '#22c55e'
const ACCEL_RED = '#ef4444'
/** Colour levels per side; the lines are drawn in runs of one level. */
export const ACCEL_LEVELS = 12

/** 0 for neutral, +1..+LEVELS accelerating, -1..-LEVELS braking. */
export function accelLevel(a: number, scale: AccelScale): number {
  if (!Number.isFinite(a) || Math.abs(a) < ACCEL_DEAD_BAND_G) return 0
  if (a > 0) {
    if (scale.maxAccel <= ACCEL_DEAD_BAND_G) return 0
    return Math.max(1, Math.min(ACCEL_LEVELS, Math.ceil((Math.min(a, scale.maxAccel) / scale.maxAccel) * ACCEL_LEVELS)))
  }
  if (scale.maxBrake <= ACCEL_DEAD_BAND_G) return 0
  return -Math.max(1, Math.min(ACCEL_LEVELS, Math.ceil((Math.min(-a, scale.maxBrake) / scale.maxBrake) * ACCEL_LEVELS)))
}

export function accelLevelColor(level: number): string {
  if (level === 0) return ACCEL_NEUTRAL
  const t = 0.25 + 0.75 * (Math.abs(level) / ACCEL_LEVELS)
  return mix(ACCEL_NEUTRAL, level > 0 ? ACCEL_GREEN : ACCEL_RED, t)
}

/** Linear mix of two hex colours, t from 0 (a) to 1 (b). */
export function mix(a: string, b: string, t: number): string {
  const pa = hex(a)
  const pb = hex(b)
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t))
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

function hex(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
}

/** The scale for a set of laps, from their GPS points. */
export function scaleForTraces(traces: LapTrace[]): { accels: Float64Array[]; scale: AccelScale } {
  const accels = traces.map((t) => gpsAccel(t.gps))
  return { accels, scale: accelScale(accels) }
}
